/**
 * @module api
 * Centralised HTTP client layer for all backend microservices.
 *
 * Each service gets its own axios instance so requests go to the correct
 * base URL and timeouts are tuned per use-case (e.g. uploads need more time).
 *
 * Auth strategy: short-lived access tokens (15 min) stored in localStorage,
 * refreshed automatically via an httpOnly cookie holding the 7-day refresh token.
 * The refresh token cookie is invisible to JS (XSS-safe); `withCredentials: true`
 * on `authApi` is what makes the browser include it on cross-origin requests.
 *
 * Concurrent 401 handling: when multiple requests fail simultaneously we only
 * attempt one refresh. Other requests queue via `subscribeTokenRefresh` and
 * resolve once the single refresh completes (`onTokenRefreshed` / `onRefreshFailed`).
 */

import { showBooked } from "@/composables/useBooked.js";
import axios from "axios";

// ── Axios instances ─────────────────────────────────────────────────────────

// Accounting service handles both sales and petty cash (merged from separate services)
const accountingApi = axios.create({
  baseURL: import.meta.env.VITE_API_ACCOUNTING_URL,
  timeout: 30000,
});

const assetsApi = axios.create({
  baseURL: import.meta.env.VITE_API_ASSETS_URL,
  timeout: 60000, // 60s timeout for file uploads
});

const marketingApi = axios.create({
  baseURL: import.meta.env.VITE_API_MARKETING_URL,
  timeout: 30000,
});

const contactsApi = axios.create({
  baseURL: import.meta.env.VITE_API_CONTACTS_URL,
  timeout: 30000,
});

// Menu service (renamed from food-cost)
const menuApi = axios.create({
  baseURL: import.meta.env.VITE_API_MENU_URL,
  timeout: 30000,
});

// Fusion Eat meals service — admin routes only (/api/meals/admin/*). The
// employee ordering routes are called from the separate meals.fusion-eat.com
// frontend, not from this app.
const mealsApi = axios.create({
  baseURL: import.meta.env.VITE_API_MEALS_URL,
  timeout: 30000,
});

// Auth client is separate: withCredentials ensures the httpOnly refresh-token
// cookie is sent on cross-origin requests (proxied through nginx in production
// to make it first-party).
const authApi = axios.create({
  baseURL: import.meta.env.VITE_API_AUTH_URL,
  timeout: 30000,
  withCredentials: true,
});

// ── Token helpers ────────────────────────────────────────────────────────────

const isTokenExpired = () => {
  const expiry = localStorage.getItem("tokenExpiry");
  if (!expiry) return true;

  const expiryDate = new Date(expiry);
  const now = new Date();
  return now >= expiryDate;
};

// Public auth pages where we should never redirect to login
const AUTH_PUBLIC_PAGES = ["/login", "/reset-password", "/activate-account", "/forgot-password"];

const isAuthPublicPage = () => AUTH_PUBLIC_PAGES.includes(window.location.pathname);

const isOnAuthServer = () => {
  return (
    window.location.hostname === "localhost" &&
    (window.location.port === "3001" || window.location.port === "")
  );
};

const getAuthServerUrl = (path = "") => {
  const authBase = import.meta.env.VITE_API_AUTH_URL || "http://localhost:3001";
  return `${authBase}${path}`;
};

// ── Request interceptors ─────────────────────────────────────────────────────
// Attach the Bearer token to every outgoing request. We always attach it and
// let the 401 response interceptor handle expiry — avoids a race where the
// token expires between the `isTokenExpired()` check and the actual request.

accountingApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Concurrent refresh queue ─────────────────────────────────────────────────
// Only one refresh attempt runs at a time. Subsequent 401s during an in-flight
// refresh subscribe here and are resolved/rejected once the refresh settles.

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const onRefreshFailed = (error) => {
  refreshSubscribers.forEach((cb) => cb(null, error));
  refreshSubscribers = [];
};

// ── 401 handler ──────────────────────────────────────────────────────────────

/**
 * Attempt a token refresh on 401, then retry the original request.
 * The `_retry` flag prevents an infinite loop if the retried request also
 * returns 401 (which would happen if the refresh token is also invalid).
 */
const handleUnauthorized = async (error, originalRequest, apiInstance) => {
  if (!originalRequest._retry) {
    originalRequest._retry = true;

    if (!isRefreshing) {
      isRefreshing = true;

      try {
        const newToken = await refreshToken();
        isRefreshing = false;
        onTokenRefreshed(newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiInstance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        onRefreshFailed(refreshError);
        if (!isAuthPublicPage()) {
          window.location.href = getAuthServerUrl("/login");
        }
        return Promise.reject(refreshError);
      }
    } else {
      // Another refresh is already in-flight — wait for it to resolve
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((newToken, err) => {
          if (err) {
            reject(err);
          } else {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(apiInstance(originalRequest));
          }
        });
      });
    }
  }

  // Second 401 on the retried request means the refresh token itself is also invalid
  // (expired, revoked, or account suspended/changed). We cannot recover client-side —
  // clear all auth state and redirect to login so the user re-authenticates.
  localStorage.removeItem("authToken");
  localStorage.removeItem("userRole");
  localStorage.removeItem("tokenExpiry");
  localStorage.removeItem("user");

  if (!isAuthPublicPage()) {
    window.location.href = getAuthServerUrl("/login");
  }
  return Promise.reject(new Error("Unauthorized - redirecting to login"));
};

// ── Response interceptors ────────────────────────────────────────────────────
// Each API instance registers the same pattern: pass through successes,
// handle 401 via the shared refresh logic, log 403 for debugging.

accountingApi.interceptors.response.use(
  // An answer that booked something in the GL by itself carries `booked` — show it (useBooked.js)
  (response) => { if (response.data?.booked) showBooked(response.data.booked); return response; },
  async (error) => {
    if (error.response) {
      const { status } = error.response;
      if (status === 401) {
        return handleUnauthorized(error, error.config, accountingApi);
      }
      if (status === 403) {
        console.error("Access forbidden: Insufficient permissions");
      }
    }
    return Promise.reject(error);
  },
);


assetsApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

assetsApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      const { status } = error.response;
      if (status === 401) {
        return handleUnauthorized(error, error.config, assetsApi);
      }
      if (status === 403) {
        console.error("Access forbidden: Insufficient permissions");
      }
    }
    return Promise.reject(error);
  },
);

marketingApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

marketingApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      const { status } = error.response;
      if (status === 401) {
        return handleUnauthorized(error, error.config, marketingApi);
      }
      if (status === 403) {
        console.error("Access forbidden: Insufficient permissions");
      }
    }
    return Promise.reject(error);
  },
);

contactsApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

contactsApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      const { status } = error.response;
      if (status === 401) {
        return handleUnauthorized(error, error.config, contactsApi);
      }
      if (status === 403) {
        console.error("Access forbidden: Insufficient permissions");
      }
    }
    return Promise.reject(error);
  },
);

menuApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

menuApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      const { status } = error.response;
      if (status === 401) {
        return handleUnauthorized(error, error.config, menuApi);
      }
      if (status === 403) {
        console.error("Access forbidden: Insufficient permissions");
      }
    }
    return Promise.reject(error);
  },
);

mealsApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

mealsApi.interceptors.response.use(
  // An answer that booked something in the GL by itself carries `booked` — show it (useBooked.js)
  (response) => { if (response.data?.booked) showBooked(response.data.booked); return response; },
  async (error) => {
    if (error.response) {
      const { status } = error.response;
      if (status === 401) {
        return handleUnauthorized(error, error.config, mealsApi);
      }
      if (status === 403) {
        console.error("Access forbidden: Insufficient permissions");
      }
    }
    return Promise.reject(error);
  },
);

// Auth API attaches the access token too (for protected /api/admin endpoints),
// but only when it's still valid — refresh calls must NOT include it to avoid
// confusing the server with an expired token in the header.
authApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token && !isTokenExpired()) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API needs the same 401 retry as other instances so that protected admin
// endpoints (/api/admin/users etc.) are retried after a token refresh.
// The refresh-token endpoint itself is excluded to prevent infinite loops.
authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !error.config.url?.includes("/api/auth/refresh-token")) {
      return handleUnauthorized(error, error.config, authApi);
    }
    return Promise.reject(error);
  }
);

// ── Token refresh ─────────────────────────────────────────────────────────────

/**
 * Exchange the httpOnly refresh-token cookie for a new access token.
 * Retries up to `maxRetries` times with exponential backoff for transient
 * network errors. Does not retry on 401 (invalid/revoked refresh token).
 * Skips entirely on auth pages to prevent redirect loops.
 */
export const refreshToken = async (maxRetries = 3) => {
  // Avoid infinite redirect loops on public pages
  const path = window.location.pathname;
  if (path === "/login" || path === "/activate-account" || path === "/reset-password" || path === "/forgot-password") {
    const err = new Error("Skipping refresh on auth page");
    err.skipRedirect = true;
    throw err;
  }

  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Cookie is sent automatically thanks to withCredentials: true on authApi
      const response = await authApi.post("/api/auth/refresh-token", {});

      const { token, expiresIn } = response.data;

      localStorage.setItem("authToken", token);

      if (expiresIn) {
        const expiry = new Date(Date.now() + getExpiryMillis(expiresIn)).toISOString();
        localStorage.setItem("tokenExpiry", expiry);
      }

      console.log("Token refreshed successfully");
      return token;
    } catch (error) {
      lastError = error;
      console.warn(`Token refresh attempt ${attempt} failed:`, error.message);

      // 401 = refresh token revoked or expired — no point retrying
      if (error.response?.status === 401) {
        break;
      }

      if (attempt < maxRetries) {
        // Exponential backoff: 2s, 4s, 8s…
        await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
  }

  localStorage.removeItem("authToken");
  localStorage.removeItem("userRole");
  localStorage.removeItem("tokenExpiry");
  localStorage.removeItem("user");

  throw lastError || new Error("Token refresh failed");
};

// ── Auto-refresh timer ────────────────────────────────────────────────────────

let refreshTimerId = null;

/**
 * Schedule a proactive token refresh 1 minute before the access token expires.
 * Called after every successful login or refresh to keep the session alive.
 * Re-schedules itself after a successful refresh so the cycle continues.
 */
export const startTokenRefreshTimer = () => {
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }

  const expiry = localStorage.getItem("tokenExpiry");
  if (!expiry) return;

  const expiryDate = new Date(expiry);
  const now = new Date();
  const timeUntilExpiry = expiryDate - now;

  // Refresh 1 minute before expiry (for 15-minute access tokens)
  const refreshTime = Math.max(timeUntilExpiry - 60 * 1000, 0);

  if (refreshTime > 0) {
    const refreshMinutes = Math.round(refreshTime / 1000 / 60);
    console.log(`Token will auto-refresh in ${refreshMinutes} minute${refreshMinutes !== 1 ? 's' : ''}`);
    refreshTimerId = setTimeout(async () => {
      try {
        await refreshToken();
        startTokenRefreshTimer(); // Reset timer with new expiry
      } catch (error) {
        console.error("Auto-refresh failed:", error);
        if (!isAuthPublicPage()) {
          window.location.href = getAuthServerUrl("/login");
        }
      }
    }, refreshTime);
  } else if (timeUntilExpiry > 0) {
    // Token expires within the 1-minute window but is still technically valid
    refreshToken()
      .then(() => startTokenRefreshTimer())
      .catch(() => {
        if (!isAuthPublicPage()) {
          window.location.href = getAuthServerUrl("/login");
        }
      });
  }
};

export const stopTokenRefreshTimer = () => {
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
};

// ── Expiry parsing ────────────────────────────────────────────────────────────

/** Convert a JWT-style duration string ("15m", "7d") to milliseconds. */
const getExpiryMillis = (expiresIn) => {
  if (!expiresIn) return 7 * 24 * 60 * 60 * 1000; // default 7 days

  const unit = expiresIn.slice(-1);
  const value = parseInt(expiresIn.slice(0, -1)) || 7;

  switch (unit) {
    case "d": return value * 24 * 60 * 60 * 1000;
    case "h": return value * 60 * 60 * 1000;
    case "m": return value * 60 * 1000;
    case "s": return value * 1000;
    default:  return 7 * 24 * 60 * 60 * 1000;
  }
};

// ── Auth state helpers ────────────────────────────────────────────────────────

export const isAuthenticated = () => {
  const token = localStorage.getItem("authToken");
  return !!token && !isTokenExpired();
};

export const getUserRole = () => {
  return localStorage.getItem("userRole");
};

export const getUser = () => {
  const userStr = localStorage.getItem("user");
  try {
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
};

/**
 * Full logout: stop the auto-refresh timer, revoke the refresh token on the
 * server (so the httpOnly cookie becomes useless), clear localStorage, and
 * redirect to login. API errors are ignored — we log out regardless.
 */
export const logout = async () => {
  stopTokenRefreshTimer();

  // Reset user preferences cache so the next user gets a fresh load
  try {
    const mod = await import("@/composables/useUserPreferences.js");
    mod.useUserPreferences().resetPreferences();
  } catch { /* composable not loaded yet */ }

  try {
    await authApi.post("/api/auth/logout");
  } catch (error) {
    console.warn("Logout API call failed:", error.message);
  }

  // Remember last visited page for post-login redirect
  const currentPath = window.location.pathname;
  if (currentPath && currentPath !== "/login" && currentPath !== "/") {
    localStorage.setItem("lastPath", currentPath);
  }

  localStorage.removeItem("authToken");
  localStorage.removeItem("userRole");
  localStorage.removeItem("tokenExpiry");
  localStorage.removeItem("user");
  localStorage.removeItem("options");

  window.location.href = getAuthServerUrl("/login");
};

export { accountingApi, assetsApi, authApi, contactsApi, marketingApi, menuApi, mealsApi, visieraOrdersApi };

// ── Visiera orders API (admin CRUD, uses accountingApi) ────────────────────
const visieraOrdersApi = {
  list: (params) => accountingApi.get("/api/accounting/sales/visiera-orders", { params }),
  get: (id) => accountingApi.get(`/api/accounting/sales/visiera-orders/${id}`),
  create: (data) => accountingApi.post("/api/accounting/sales/visiera-orders", data),
  update: (id, data) => accountingApi.put(`/api/accounting/sales/visiera-orders/${id}`, data),
  delete: (id) => accountingApi.delete(`/api/accounting/sales/visiera-orders/${id}`),
};

export default {
  accountingApi,
  authApi,
  assetsApi,
  marketingApi,
  contactsApi,
  menuApi,
  mealsApi,
  visieraOrdersApi,
  refreshToken,
  startTokenRefreshTimer,
  stopTokenRefreshTimer,
  isAuthenticated,
  getUserRole,
  getUser,
  logout,
};
